// Countdown to a fixed instant, for the pages that hold a garment back.
//
// The deadline arrives as an ISO 8601 string carrying its own UTC offset
// (2026-09-01T00:00:00+02:00), never a bare date. A bare date is read in the
// visitor's own timezone, so the drop would appear to happen at midnight
// wherever the visitor happens to be standing - nine hours apart between Paris
// and Los Angeles, on a page whose whole job is to say when. The offset pins it
// to one instant worldwide.
//
// Liquid renders the opening figures server-side, so the block is already
// correct before this file runs and stays correct for anyone who never gets it.
// Shopify can serve those figures from its page cache, though, which is why the
// first thing here is a recompute rather than a wait for the first tick: a
// cached render is stale by however long it sat in the cache.

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

class AccoladeCountdown extends HTMLElement {
  /** @type {number | null} */
  #timer = null;

  /** @type {number} */
  #deadline = NaN;

  connectedCallback() {
    this.#deadline = Date.parse(this.dataset.deadline ?? '');

    // An unparseable deadline leaves the server-rendered figures standing
    // rather than blanking them. They are wrong by at most the cache age,
    // which beats an empty box where the date should be.
    if (Number.isNaN(this.#deadline)) return;

    this.#tick();
    this.#timer = window.setInterval(() => this.#tick(), SECOND);
  }

  disconnectedCallback() {
    this.#stop();
  }

  #stop() {
    if (this.#timer !== null) window.clearInterval(this.#timer);
    this.#timer = null;
  }

  #tick() {
    const remaining = this.#deadline - Date.now();

    if (remaining <= 0) {
      // Hiding beats freezing on four zeros, which reads as a broken widget
      // rather than as an arrival. The rest of the page still carries the copy.
      this.hidden = true;
      this.#stop();
      return;
    }

    // Each unit is derived from `remaining` rather than decremented, so a
    // browser that throttles timers in a background tab catches up on the next
    // tick instead of drifting for as long as the tab was hidden.
    this.#set('days', Math.floor(remaining / DAY), 1);
    this.#set('hours', Math.floor(remaining / HOUR) % 24, 2);
    this.#set('minutes', Math.floor(remaining / MINUTE) % 60, 2);
    this.#set('seconds', Math.floor(remaining / SECOND) % 60, 2);
  }

  /**
   * @param {string} unit
   * @param {number} value
   * @param {number} width - digits to pad to; days are left unpadded, since
   *   they run to three and a leading zero on a single digit looks like a typo.
   */
  #set(unit, value, width) {
    const el = this.querySelector(`[data-unit="${unit}"]`);

    // Seconds are optional, so a missing element is expected, not an error.
    if (!el) return;

    const text = String(value).padStart(width, '0');
    if (el.textContent !== text) el.textContent = text;
  }
}

// Guarded because the block loads this file itself rather than relying on the
// layout, so two countdowns on one page would otherwise redefine the element
// and throw.
if (!customElements.get('accolade-countdown')) {
  customElements.define('accolade-countdown', AccoladeCountdown);
}
